// app/utils/twoPlusOne.ts
import type { CartItemType } from '~/utils/api/cart';
import type { ProductType } from '~/utils/api/common';

/**
 * Зеркалит расчёт акции «2+1» на бэкенде. basket/synchronize (анонимная
 * корзина) акцию не применяет — в отличие от авторизованного basket, где
 * бэкенд сам сплитит корзину. Правило бэкенда: из N единиц товаров-участников
 * floor(N/3) самых дешёвых становятся строками за 1 ₽ (twoPlusOneWinner,
 * basePrice = полная цена), остальные остаются по полной цене.
 */
export const applyTwoPlusOnePromo = (items: CartItemType[], products: ProductType[]): CartItemType[] => {
  // Ответ уже со сплитом (бэкенд починил synchronize) — не применяем повторно.
  if (items.some((item) => item.twoPlusOneWinner)) return items;

  const isParticipant = (item: CartItemType) =>
    !item.isGift && products.some((product) => product.id === item.productId && product.isTwoPlusOne);

  const participantUnits = items.flatMap((item) =>
    isParticipant(item) ? Array.from({ length: item.quantity }, () => item) : [],
  );

  const winnersCount = Math.floor(participantUnits.length / 3);
  if (!winnersCount) return items;

  // Самые дешёвые единицы — первыми; при равной цене порядок строк сохраняется (sort стабилен).
  const sorted = [...participantUnits].sort((a, b) => a.price - b.price);
  const winnerQtyBySku = new Map<number, number>();
  for (const unit of sorted.slice(0, winnersCount)) {
    winnerQtyBySku.set(unit.skuId, (winnerQtyBySku.get(unit.skuId) ?? 0) + 1);
  }

  const result: CartItemType[] = [];
  for (const item of items) {
    const winners = winnerQtyBySku.get(item.skuId) ?? 0;
    if (!winners) {
      result.push(item);
      continue;
    }
    if (item.quantity > winners) result.push({ ...item, quantity: item.quantity - winners });
    result.push({ ...item, quantity: winners, price: 1, basePrice: item.price, twoPlusOneWinner: true });
  }
  return result;
};

/**
 * Сплит-корзина (winner за 1 ₽ отдельной строкой) → одна строка на sku с
 * суммарным количеством. Для payload'ов basket/synchronize и basket/transfer:
 * бэкенд ждёт по одной записи на товар, дубликаты id посчитались бы неверно.
 */
export const mergeSameSkuItems = (items: CartItemType[]): CartItemType[] => {
  const merged = new Map<number, CartItemType>();
  for (const item of items) {
    const existing = merged.get(item.skuId);
    if (existing) existing.quantity += item.quantity;
    else merged.set(item.skuId, { ...item });
  }
  return [...merged.values()];
};

/** Ключ строки корзины для v-for: в сплите у одного sku две строки (winner отдельно). */
export const cartItemKey = (item: Pick<CartItemType, 'skuId' | 'twoPlusOneWinner'>): string =>
  `${item.skuId}-${item.twoPlusOneWinner ? 'winner' : 'full'}`;
